import React, { useState } from 'react';
import { Typography, Box, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { Close as CloseIcon } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { TaskFieldSelector, TaskFieldColumn } from './TaskFieldSelector';
import { GetTasksWithFields } from '../../../admin/tasks-import/__generated__/GetTasksWithFields';

interface TaskColumnSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (selectedFields: TaskFieldColumn[]) => void;
  initialSelectedFields: TaskFieldColumn[];
  tasksData?: GetTasksWithFields;
}

const useStyles = makeStyles(() =>
  createStyles({
    dialogTitle: {
      paddingBottom: '8px',
    },
    description: {
      fontSize: '14px',
      color: '#6b7280',
      marginBottom: '24px',
    },
    dialogContent: {
      paddingTop: '24px',
      paddingBottom: '4em',
    },
    dialogActions: {
      padding: '16px 24px',
      borderTop: '1px solid #e5e7eb',
    },
    taskBox: {
      padding: '16px',
      borderBottom: '1px solid #e5e7eb',
    },
    fieldBox: {
      marginLeft: '48px',
      marginRight: '20px',
      paddingLeft: '16px',
      borderLeft: '2px solid #e5e7eb',
      marginTop: '8px',
    },
  })
);

export const TaskColumnSelectionDialog = (props: TaskColumnSelectionDialogProps) => {
  const { open, onClose, onSave, initialSelectedFields, tasksData } = props;
  const { isMobile } = useCampfireTheme();
  const classes = useStyles();

  const [selectedFields, setSelectedFields] = useState<Map<string, TaskFieldColumn>>(
    new Map(initialSelectedFields.map((field) => [field.fieldId, field]))
  );

  // Use tasks data from props (shared query in parent)
  const tasks = tasksData?.vm?.tasks || [];

  const handleToggleField = (task: any, field: any) => {
    const fieldKey = field.taskFieldId;
    setSelectedFields((prev) => {
      const next = new Map(prev);
      if (next.has(fieldKey)) {
        next.delete(fieldKey);
      } else {
        next.set(fieldKey, {
          fieldId: field.taskFieldId,
          fieldName: field.name,
          fieldType: field.__typename,
          taskId: task.taskId,
          taskTitle: task.title,
        });
      }
      return next;
    });
  };

  const handleToggleAllTaskFields = (task: any, fields: any[]) => {
    const allSelected = fields.every((field) => selectedFields.has(field.taskFieldId));

    setSelectedFields((prev) => {
      const next = new Map(prev);
      if (allSelected) {
        // Deselect all fields from this task
        fields.forEach((field) => {
          next.delete(field.taskFieldId);
        });
      } else {
        // Select all fields from this task
        fields.forEach((field) => {
          next.set(field.taskFieldId, {
            fieldId: field.taskFieldId,
            fieldName: field.name,
            fieldType: field.__typename,
            taskId: task.taskId,
            taskTitle: task.title,
          });
        });
      }
      return next;
    });
  };

  const handleSave = () => {
    const fields = Array.from(selectedFields.values());
    onSave(fields);
    handleClose();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullScreen={isMobile} maxWidth='md' fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Box>
            <Typography variant='h6'>Select Task Fields to Display as Columns</Typography>
          </Box>
          <IconButton onClick={handleClose} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Typography className={classes.description}>
          Select the task fields you want to display as columns in the volunteer database. Expand tasks to view and
          select their fields.
        </Typography>

        <TaskFieldSelector
          tasks={tasks}
          selectedFields={selectedFields}
          onToggleField={handleToggleField}
          onToggleAllTaskFields={handleToggleAllTaskFields}
        />
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <TabletButton
          variant='text'
          color='error'
          onClick={handleClose}
          style={{ marginRight: 8, textTransform: 'none' }}
        >
          Cancel
        </TabletButton>

        <TabletButton variant='contained' color='primary' onClick={handleSave} style={{ textTransform: 'none' }}>
          Save
        </TabletButton>
      </DialogActions>
    </Dialog>
  );
};
